// Aggregate rankings do not track visitors. Only current native product cards are hydrated.
const MAX_GROUPS = 21;
const MAX_HANDLES = 12;
const HANDLE = /^[\p{L}\p{N}][\p{L}\p{N}-]{0,254}$/u;

export function parseTrendingConfig(raw) {
  const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (!data || data.version !== 1 || typeof data.locale !== 'string' || !/^[A-Z]{2}$/.test(data.country) || !/^[A-Z]{3}$/.test(data.currency)) throw new TypeError('Invalid storefront context');
  if (typeof data.rootUrl !== 'string' || !data.rootUrl.startsWith('/') || data.rootUrl.startsWith('//') || /[?#\\]/.test(data.rootUrl)) throw new TypeError('Invalid storefront root');
  const groups = [];
  const seen = new Set();
  for (const candidate of (Array.isArray(data.groups) ? data.groups : []).slice(0, MAX_GROUPS)) {
    if (!candidate || !(candidate.code === 'GLOBAL' || /^[A-Z]{2}$/.test(candidate.code)) || seen.has(candidate.code)) continue;
    if (candidate.code !== 'GLOBAL' && data.feedReady !== true) continue;
    const handles = data.feedReady === true ? [...new Set((Array.isArray(candidate.handles) ? candidate.handles : []).filter(h => typeof h === 'string' && HANDLE.test(h)))].slice(0, MAX_HANDLES) : [];
    if (candidate.code !== 'GLOBAL' && !handles.length) continue;
    seen.add(candidate.code);
    groups.push({ code: candidate.code, label: typeof candidate.label === 'string' ? candidate.label.slice(0, 100) : candidate.code, handles });
  }
  if (!groups.some(group => group.code === 'GLOBAL')) groups.unshift({ code: 'GLOBAL', label: 'Global', handles: [] });
  const labels = {};
  for (const key of ['loading', 'error', 'empty', 'ready']) labels[key] = typeof data.labels?.[key] === 'string' ? data.labels[key].slice(0, 250) : '';
  const cj = data.cjSelection?.source === 'cj-listed' ? data.cjSelection : {};
  const cjSelection = { source: 'cj-listed', handles: [...new Set((Array.isArray(cj.handles) ? cj.handles : []).filter(h => typeof h === 'string' && HANDLE.test(h)))].slice(0,10) };
  for (const key of ['title','note','disclaimer','empty','pick']) cjSelection[key] = typeof cj[key] === 'string' ? cj[key].slice(0,250) : '';
  const selectionSource = data.feedReady === true && groups.some(group => group.handles.length) ? 'sales' : cjSelection.handles.length ? 'cj-listed' : 'empty';
  return { cjSelection, selectionSource, version: 1, feedReady: data.feedReady === true, locale: data.locale, country: data.country, currency: data.currency, rootUrl: data.rootUrl, pinnedHandle: typeof data.pinnedHandle === 'string' && HANDLE.test(data.pinnedHandle) ? data.pinnedHandle : null, groups, labels };
}

export function safeServiceUrl(raw, location) {
  const url = new URL(raw, location.href);
  // Shopify asset_url may use the shop /cdn or its Shopify CDN, never an arbitrary script host.
  if (url.protocol !== 'https:' || !(url.origin === location.origin || url.hostname === 'cdn.shopify.com') || !url.pathname.endsWith('/ricordo-trending-product-service.js')) throw new TypeError('Invalid service asset');
  return url.href;
}

export function appendNativeCards(list, cards, ownerDocument = document, excluded = new Set()) {
  let added = 0;
  for (const card of cards || []) {
    if (!card || typeof card.handle !== 'string' || !HANDLE.test(card.handle) || excluded.has(card.handle) || !card.element || card.element.tagName?.toLowerCase() !== 'product-card') continue;
    const item = ownerDocument.createElement('li');
    item.dataset.rcTrendingHandle = card.handle;
    item.append(ownerDocument.importNode(card.element, true));
    list.append(item);
    excluded.add(card.handle);
    added++;
  }
  return added;
}

// The original Discovery pipeline now owns the single home hero.
export function mountDiscoveryCarousel(host) {
  const root = host.querySelector('[data-rc-discovery-carousel]');
  if (!root) return null;
  const doc = host.ownerDocument;
  const view = doc.defaultView || window;
  const stage = root.querySelector('[data-rc-discovery-slides]');
  const slides = [...root.querySelectorAll('[data-rc-discovery-slide]')];
  const dots = [...root.querySelectorAll('[data-rc-discovery-dot]')];
  const controls = root.querySelector('[data-rc-discovery-controls]');
  const arrows = [...root.querySelectorAll('[data-rc-discovery-arrow]')];
  const rotation = root.querySelector('[data-rc-discovery-rotation]');
  const announcement = root.querySelector('[data-rc-discovery-announcement]');
  if (!stage || slides.length < 2 || dots.length !== slides.length || !rotation) return null;
  const reduced = view.matchMedia('(prefers-reduced-motion: reduce)');
  let current = 0;
  let userPaused = reduced.matches;
  let hovered = false;
  let transitioning = false;
  let queuedManual = null;
  let destroyed = false;
  let timer = null;
  let transitionTimer = null;
  let resizeFrame = null;
  let focusTimer = null;
  const stop = () => { view.clearTimeout(timer); timer = null; };
  const focusPaused = () => { const active=doc.activeElement; return active && root.contains(active) && active!==rotation && !active.matches('[data-rc-discovery-dot],[data-rc-discovery-arrow]') && active.matches('a,button,input,select,textarea'); };
  const paused = () => userPaused || reduced.matches || hovered || focusPaused() || doc.hidden || host.querySelector('[data-rc-discovery-dialog]')?.open;
  const updateControls = () => {
    for (let i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === current);
      dots[i].classList.toggle('is-near', i === current || i === (current + 1) % dots.length || i === (current - 1 + dots.length) % dots.length);
      slides[i].setAttribute('aria-label', `${i + 1} / ${slides.length} · ${dots[i].getAttribute('aria-label') || ''}`);
      if (i === current) dots[i].setAttribute('aria-current', 'true'); else dots[i].removeAttribute('aria-current');
    }
    const stopped = userPaused || reduced.matches;
    const label = stopped ? root.dataset.playLabel : root.dataset.pauseLabel;
    rotation.setAttribute('aria-label', label || (stopped ? 'Play slideshow' : 'Pause slideshow'));
    rotation.title = rotation.getAttribute('aria-label');
    rotation.querySelector('[data-rc-pause-icon]')?.toggleAttribute('hidden', stopped);
    rotation.querySelector('[data-rc-play-icon]')?.toggleAttribute('hidden', !stopped);
    rotation.hidden = reduced.matches;
  };
  const schedule = () => {
    stop();
    if (destroyed || paused() || transitioning) return;
    timer = view.setTimeout(() => { if (!paused()) select((current + 1) % slides.length, false); }, 8000);
  };
  const measure = () => {
    if (destroyed || transitioning || !stage.clientWidth) return;
    // Keep the stage in flow throughout measurement; clearing its height can trigger scroll anchoring.
    if (!stage.style.getPropertyValue('--rc-discovery-stage-height')) {
      stage.style.setProperty('--rc-discovery-stage-height', Math.ceil(stage.getBoundingClientRect().height) + 'px');
    }
    let maximum = 0;
    for (const slide of slides) {
      slide.classList.add('is-measuring');
      maximum = Math.max(maximum, slide.getBoundingClientRect().height);
      slide.classList.remove('is-measuring');
    }
    stage.style.setProperty('--rc-discovery-stage-height', Math.ceil(maximum) + 'px');
  };
  const requestMeasure = () => {
    if (resizeFrame !== null) view.cancelAnimationFrame(resizeFrame);
    resizeFrame = view.requestAnimationFrame(() => { resizeFrame = null; measure(); });
  };
  const select = (index, manual = true) => {
    if (destroyed || index < 0 || index >= slides.length) return;
    // Keep the last deliberate choice made during the short fade, including index zero.
    if (transitioning) { if (manual) queuedManual = index; return; }
    // Manual selection restarts the normal dwell; only the explicit pause control latches.
    if (index === current) { updateControls(); schedule(); return; }
    stop();
    const previous = slides[current];
    const next = slides[index];
    current = index;
    transitioning = !reduced.matches;
    host.dispatchEvent(new CustomEvent('rc:discovery-changing'));
    previous.inert = true;
    previous.setAttribute('aria-hidden', 'true');
    previous.classList.remove('is-active');
    next.hidden = false;
    next.inert = true;
    next.setAttribute('aria-hidden', 'true');
    updateControls();
    const finish = () => {
      previous.hidden = true;
      previous.classList.remove('is-leaving');
      next.classList.remove('is-entering');
      next.classList.add('is-active');
      next.inert = false;
      next.removeAttribute('aria-hidden');
      transitioning = false;
      if (manual && announcement) {
        announcement.setAttribute('aria-live', 'polite');
        announcement.textContent = next.getAttribute('aria-label');
      } else if (announcement) announcement.setAttribute('aria-live', 'off');
      requestMeasure();
      if (queuedManual !== null) { const requested = queuedManual; queuedManual = null; select(requested, true); return; }
      schedule();
    };
    if (reduced.matches) { finish(); return; }
    previous.classList.add('is-leaving');
    next.classList.add('is-entering');
    view.requestAnimationFrame(() => view.requestAnimationFrame(() => {
      if (destroyed) return;
      next.classList.add('is-active');
      next.classList.remove('is-entering');
    }));
    transitionTimer = view.setTimeout(finish, 560);
  };
  const onDot = event => select(Number(event.currentTarget.dataset.rcDiscoveryDot));
  const onDotKey = event => {
    const index = dots.indexOf(event.currentTarget);
    const rtl = host.getAttribute('dir') === 'rtl';
    let target;
    if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = dots.length - 1;
    else if (event.key === 'ArrowRight') target = (index + (rtl ? -1 : 1) + dots.length) % dots.length;
    else if (event.key === 'ArrowLeft') target = (index + (rtl ? 1 : -1) + dots.length) % dots.length;
    else return;
    event.preventDefault(); select(target); dots[target].focus({ preventScroll: true });
  };
  const onArrow = event => select((current + Number(event.currentTarget.dataset.rcDiscoveryArrow) + slides.length) % slides.length);
  const onRotate = () => { userPaused = !userPaused; updateControls(); schedule(); };
  const hoverTarget = target => target?.closest?.('a,button,input,select,textarea,product-form-component');
  const onEnter = event => { if(event.pointerType==='touch')return; const target=hoverTarget(event.target); hovered=Boolean(target&&root.contains(target)); if(hovered)stop(); };
  const onLeave = event => { const target=hoverTarget(event.relatedTarget); hovered=Boolean(target&&root.contains(target)); if(!hovered)schedule(); };
  const onFocus = () => { if(focusPaused())stop(); else schedule(); };
  const onBlur = () => { view.clearTimeout(focusTimer); focusTimer = view.setTimeout(schedule, 0); };
  const onVisibility = () => { if (doc.hidden) stop(); else schedule(); };
  const onReduced = () => { if (reduced.matches) userPaused = true; updateControls(); schedule(); };
  const onInteraction = event => {
    if (event.target.closest('a,input,select,textarea,.rcsh-purchase button')) { stop(); schedule(); }
  };
  for (const dot of dots) { dot.addEventListener('click', onDot); dot.addEventListener('keydown', onDotKey); }
  for (const arrow of arrows) arrow.addEventListener('click', onArrow);
  rotation.addEventListener('click', onRotate);
  root.addEventListener('pointerover', onEnter); root.addEventListener('pointerout', onLeave);
  root.addEventListener('focusin', onFocus); root.addEventListener('focusout', onBlur);
  root.addEventListener('click', onInteraction);
  root.addEventListener('load', requestMeasure, true);
  doc.addEventListener('visibilitychange', onVisibility);
  reduced.addEventListener?.('change', onReduced);
  view.addEventListener('resize', requestMeasure);
  host.querySelector('[data-rc-discovery-dialog]')?.addEventListener('close', schedule);
  doc.fonts?.ready?.then(() => { if (!destroyed) requestMeasure(); });
  if (controls) controls.hidden = false;
  updateControls(); requestMeasure(); schedule();
  return { select, measure: requestMeasure, getState: () => ({active:slides[current].dataset.rcDiscoverySlide,paused:paused() || timer === null,transitioning,reduced:reduced.matches}), destroy() {
    destroyed = true; stop(); view.clearTimeout(transitionTimer); view.clearTimeout(focusTimer); view.cancelAnimationFrame(resizeFrame);
    for (const dot of dots) { dot.removeEventListener('click', onDot); dot.removeEventListener('keydown', onDotKey); }
    for (const arrow of arrows) arrow.removeEventListener('click', onArrow);
    rotation.removeEventListener('click', onRotate);
    root.removeEventListener('pointerover', onEnter); root.removeEventListener('pointerout', onLeave);
    root.removeEventListener('focusin', onFocus); root.removeEventListener('focusout', onBlur);
    root.removeEventListener('click', onInteraction); root.removeEventListener('load', requestMeasure, true);
    doc.removeEventListener('visibilitychange', onVisibility); reduced.removeEventListener?.('change', onReduced);
    view.removeEventListener('resize', requestMeasure);
    host.querySelector('[data-rc-discovery-dialog]')?.removeEventListener('close', schedule);
  } };
}

// Only lightweight native-product links enter these two slots; purchase forms stay outside.
export function mountProductRotations(host, carousel) {
  if (!carousel) return null;
  const doc = host.ownerDocument, view = doc.defaultView || window;
  const root = host.querySelector('[data-rc-discovery-carousel]');
  const states = new Map();
  const excluded = new Map();
  const isExcluded=(name,handle)=>excluded.get(name)?.has(handle)===true;
  const handleOf = item => item?.dataset.rcProductHandle || new URL(item?.getAttribute('href') || '/', 'https://ricordo.us').pathname.split('/').pop();
  let destroyed = false, timer = null, measureFrame = null;
  const fillVacancies=state=>{
    for(let i=0;i<2;i++){if(state.items[state.visible[i]])continue;const index=state.items.findIndex((item,j)=>!state.visible.includes(j)&&!isExcluded(state.name,handleOf(item)));const slot=state.slots.children[i];slot.hidden=index<0;if(index>=0){const item=state.items[index];item.hidden=false;item.inert=false;slot.append(item);state.visible[i]=index;state.seen.add(handleOf(item))}}
  };
  const drain = state => {
    if(!state.backlog.length)return;
    const visible=state.visible.map(i=>state.items[i]),next=state.items[state.cursor%state.items.length];let changed=false;
    for(const item of [...state.items]){
      if(state.items.length<48)break;
      if(!visible.includes(item)&&state.seen.has(handleOf(item))&&!isExcluded(state.name,handleOf(item))){item.remove();state.items.splice(state.items.indexOf(item),1);changed=true;}
    }
    while(state.items.length<48&&state.backlog.length){const item=state.backlog.shift();item.hidden=true;item.inert=true;state.pool.append(item);state.items.push(item);changed=true;}
    if(changed){state.visible=visible.map(item=>state.items.indexOf(item));state.cursor=Math.max(0,state.items.indexOf(next));state.history=[];fillVacancies(state);controls(state);host.dispatchEvent(new CustomEvent('rc:discovery-window'));requestMeasure();}
  };

  const finish = state => {
    if (!state.transition) return;
    view.clearTimeout(state.transition.timer);
    const {old, next} = state.transition;
    old.classList.remove('rc-product-out'); old.hidden = true; old.inert = true; state.pool.append(old);
    next.classList.remove('rc-product-in'); next.hidden = false; next.inert = false;
    state.transition = null;
    drain(state);
  };
  const measure = () => {
    if (destroyed || carousel.getState().transitioning) return;
    for (const state of states.values()) {
      const slide = state.container.closest('[data-rc-discovery-slide]');
      if (slide?.hidden) slide.classList.add('is-measuring');
      const width = [...state.slots.children].find(slot=>!slot.hidden)?.getBoundingClientRect().width || 0;
      if (width > 0) {
        const probe = doc.createElement('div');
        probe.className = 'rc-product-rotation__measure'; probe.inert = true; probe.setAttribute('aria-hidden', 'true'); probe.style.width = width + 'px';
        state.container.append(probe);
        let height = 0;
        for (const item of state.items) {
          const clone = item.cloneNode(true); clone.hidden = false; clone.inert = true;
          clone.classList.remove('rc-product-in', 'rc-product-out'); clone.removeAttribute('id');
          for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
          probe.replaceChildren(clone); height = Math.max(height, clone.getBoundingClientRect().height);
        }
        probe.remove();
        if (height) state.slots.style.setProperty('--rc-product-slot-height', Math.ceil(height) + 'px');
      }
      if (slide?.hidden) slide.classList.remove('is-measuring');
    }
    carousel.measure();
  };
  const requestMeasure = () => { view.cancelAnimationFrame(measureFrame); measureFrame = view.requestAnimationFrame(measure); };
  const controls = state => { state.previous.disabled = state.history.length === 0; state.controls.hidden = state.items.length <= 2; };
  const replaceSlot = (state, target, incoming, animate, manual) => {
    const slot = state.slots.children[target], old = slot.querySelector('a'), next = state.items[incoming];
    if (!old || old === next) return;
    old.inert = true; next.hidden = false; next.inert = true; slot.append(next);
    state.transition = {old, next, manual, timer:null};
    if (!animate) { finish(state); return; }
    old.classList.add('rc-product-out'); next.classList.add('rc-product-in');
    // The boxes never move; only their current contents fade and lift slightly.
    view.requestAnimationFrame(() => view.requestAnimationFrame(() => {
      if (!state.transition || destroyed) return;
      next.classList.remove('rc-product-in');
    }));
    state.transition.timer = view.setTimeout(() => finish(state), 460);
  };
  const rotate = (state, backwards = false, manual = false) => {
    const outer = carousel.getState();
    if (destroyed || state.items.length <= 2 || state.transition || outer.transitioning) return;
    if (state.container.closest('[data-rc-discovery-slide]')?.dataset.rcDiscoverySlide !== outer.active) return;
    if (!manual && outer.paused) return;
    let target, incoming;
    if (backwards) {
      const prior = state.history.pop(); if (!prior) return;
      target = prior.visible.findIndex((value, index) => value !== state.visible[index]);
      if (target < 0) return;
      incoming = prior.visible[target]; state.visible = prior.visible; state.cursor = prior.cursor; state.target = prior.target;
    } else {
      let candidate = state.cursor;
      let attempts=0;
      while(state.visible.includes(candidate)||isExcluded(state.name,handleOf(state.items[candidate]))){candidate=(candidate+1)%state.items.length;if(++attempts>=state.items.length)return;}
      state.history.push({visible:[...state.visible],cursor:state.cursor,target:state.target});
      if (state.history.length > 32) state.history.shift();
      target = state.target; incoming = candidate; state.visible[target] = candidate;
      state.cursor = (candidate + 1) % state.items.length; state.target = (target + 1) % 2;
    }
    state.elapsed = 0;
    state.seen.add(handleOf(state.items[incoming]));
    replaceSlot(state, target, incoming, !outer.reduced, manual);
    controls(state);
    if (manual) {state.status.textContent = state.items[incoming].textContent.trim();if(state.name==='new')host.dispatchEvent(new CustomEvent('rc:discovery-more'));}
  };
  const destroyState = state => {
    finish(state); state.previous.removeEventListener('click', state.onPrevious); state.next.removeEventListener('click', state.onNext);
  };
  const refresh = () => {
    for (const container of host.querySelectorAll('[data-rc-product-rotation]')) {
      const existing = states.get(container);
      if (existing && container.contains(existing.slots)) {
        const additions=[...container.children].filter(item=>item.matches?.('a.rc-discovery-product,a.rc-discovery-card__preview'));
        let firstAdded=-1;
        for(const item of additions){const handle=handleOf(item),index=existing.items.findIndex(old=>handleOf(old)===handle);
          const queued=existing.backlog.findIndex(old=>handleOf(old)===handle);if(queued>=0){existing.backlog[queued]=item;item.remove();continue;}
          if(index>=0){const old=existing.items[index];if(carousel.getState().active!==existing.name){item.hidden=old.hidden;item.inert=old.inert;old.replaceWith(item);existing.items[index]=item}else item.remove();continue;}
          item.hidden=true;item.inert=true;if(existing.items.length>=48){item.remove();existing.backlog.push(item)}else{existing.pool.append(item);if(firstAdded<0)firstAdded=existing.items.length;existing.items.push(item)}
        }
        if(container.dataset.rcPrepend==='true'&&firstAdded>=0)existing.cursor=firstAdded;
        delete container.dataset.rcPrepend;
        drain(existing);fillVacancies(existing);
        controls(existing);continue;
      }
      if (existing) { destroyState(existing); states.delete(container); }
      const items = [...container.children].filter(item => item.tagName?.toLowerCase() === 'a' && item.matches('.rc-discovery-product,.rc-discovery-card__preview') && !item.querySelector('form,product-form-component'));
      if (!items.length) continue;
      const slots = doc.createElement('div'); slots.className = 'rc-product-rotation__slots';
      const pool = doc.createElement('div'); pool.className = 'rc-product-rotation__pool'; pool.hidden = true; pool.inert = true;
      for (let index = 0; index < 2; index++) {
        const slot = doc.createElement('div'); slot.className = 'rc-product-rotation__slot'; slot.dataset.rcProductSlot = String(index);
        if (items[index]) { items[index].hidden = false; items[index].inert = false; slot.append(items[index]); } else slot.hidden = true;
        slots.append(slot);
      }
      for (const item of items.slice(2)) { item.hidden = true; item.inert = true; pool.append(item); }
      const navigation = doc.createElement('div'); navigation.className = 'rc-product-rotation__controls';
      const previous = doc.createElement('button'), next = doc.createElement('button');
      previous.type = next.type = 'button'; previous.dataset.rcProductsPrevious = ''; next.dataset.rcProductsNext = '';
      previous.setAttribute('aria-label', root.dataset.productsPrevious || 'Previous products'); next.setAttribute('aria-label', root.dataset.productsNext || 'Next products');
      previous.textContent = '←'; next.textContent = '→'; navigation.append(previous, next);
      const status = doc.createElement('span'); status.className = 'visually-hidden'; status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true');
      container.replaceChildren(slots, pool, navigation, status);
      const state = {backlog:[],name:container.dataset.rcProductRotation,seen:new Set(items.slice(0,2).map(handleOf)),container,items,slots,pool,controls:navigation,previous,next,status,visible:items.slice(0,2).map((_,i)=>i),cursor:2,target:0,history:[],transition:null,elapsed:0};
      state.onPrevious = () => rotate(state, true, true); state.onNext = () => rotate(state, false, true);
      previous.addEventListener('click', state.onPrevious); next.addEventListener('click', state.onNext);
      controls(state); states.set(container,state);
    }
    requestMeasure();
  };
  const suspend = () => { for (const state of states.values()) { finish(state); state.elapsed = 0; } };
  const onInteraction = event => {if(event.target.closest?.('a,button,input,select,textarea'))suspend()};
  const tick = () => {
    if (destroyed) return;
    const outer = carousel.getState();
    for (const state of states.values()) {
      const active = state.container.closest('[data-rc-discovery-slide]')?.dataset.rcDiscoverySlide === outer.active;
      if(active&&state.name==='new'&&!outer.paused&&state.backlog.length===0&&state.items.filter(item=>!state.seen.has(handleOf(item))).length<=4)host.dispatchEvent(new CustomEvent('rc:discovery-more'));
      if (!active || outer.transitioning || outer.reduced || state.items.length <= 2) { state.elapsed = 0; finish(state); continue; }
      // A deliberate Next/Previous click pauses autoplay, but its own short fade may finish.
      if (outer.paused) { state.elapsed = 0; if (state.transition && !state.transition.manual) finish(state); continue; }
      state.elapsed += 500;
      if (state.elapsed >= 3000) rotate(state);
    }
    timer = view.setTimeout(tick,500);
  };
  host.addEventListener('rc:discovery-changing', suspend);
  root.addEventListener('pointerover', onInteraction); root.addEventListener('focusin', onInteraction);
  root.addEventListener('load', requestMeasure, true); view.addEventListener('resize', requestMeasure);
  doc.fonts?.ready?.then(() => { if (!destroyed) requestMeasure(); });
  refresh(); timer = view.setTimeout(tick,500);
  const remove=(name,handles)=>{
    const state=[...states.values()].find(s=>s.name===name);if(!state)return;finish(state);
    const removed=new Set(handles),oldVisible=state.visible.map(i=>state.items[i]),oldNext=state.items[state.cursor%state.items.length];
    for(const item of [...state.items,...state.backlog])if(removed.has(handleOf(item)))item.remove();
    state.items=state.items.filter(item=>!removed.has(handleOf(item)));state.backlog=state.backlog.filter(item=>!removed.has(handleOf(item)));
    const nextVisible=oldVisible.map(item=>state.items.includes(item)?item:null);
    for(let i=0;i<2;i++)if(!nextVisible[i])nextVisible[i]=state.items.find(item=>!nextVisible.includes(item)&&!isExcluded(name,handleOf(item)))||null;
    for(let i=0;i<2;i++){const slot=state.slots.children[i],item=nextVisible[i];slot.replaceChildren();slot.hidden=!item;if(item){item.hidden=false;item.inert=false;slot.append(item)}}
    for(const item of state.items)if(!nextVisible.includes(item)){item.hidden=true;item.inert=true;state.pool.append(item)}
    state.visible=nextVisible.map(item=>state.items.indexOf(item));state.cursor=Math.max(0,state.items.indexOf(oldNext));state.history=[];controls(state);drain(state);requestMeasure();
  };
  return {remove,refresh,measure:requestMeasure, visibleHandles(name){const state=[...states.values()].find(s=>s.name===name);return state?state.visible.filter(i=>i>=0).map(i=>handleOf(state.items[i])):[]}, poolHandles(name){const state=[...states.values()].find(s=>s.name===name);return state?state.items.map(handleOf):[]}, retainedHandles(name){const state=[...states.values()].find(s=>s.name===name);return state?[...state.items,...state.backlog].map(handleOf):[]}, upcomingHandles(name,count=2){const state=[...states.values()].find(s=>s.name===name);if(!state)return[];const found=[];for(let i=0;i<state.items.length&&found.length<count;i++){const index=(state.cursor+i)%state.items.length,handle=handleOf(state.items[index]);if(!state.visible.includes(index)&&!isExcluded(name,handle))found.push(handle)}return found}, exclude(name,handle){excluded.set(name,new Set(Array.isArray(handle)?handle:[handle]))}, replace(name,nodes){const container=host.querySelector(`[data-rc-product-rotation="${name}"]`);if(!container)return;const state=states.get(container);if(state){destroyState(state);states.delete(container)}container.replaceChildren(...nodes.map(n=>doc.importNode(n,true)));refresh()},destroy(){destroyed=true;view.clearTimeout(timer);view.cancelAnimationFrame(measureFrame);for(const state of states.values())destroyState(state);states.clear();host.removeEventListener('rc:discovery-changing',suspend);root.removeEventListener('pointerover',onInteraction);root.removeEventListener('focusin',onInteraction);root.removeEventListener('load',requestMeasure,true);view.removeEventListener('resize',requestMeasure);}};
}

// The Apple form is moved once, never copied. Other purchase forms are rendered by Shopify under their own IDs.
export function mountNewHighlight(host,carousel,rotations,{pinnedHandle,loadFeature,panelName='new',initialSelector=':scope>.rcsh-spotlight-wrap',reserveUpcoming=2,reservePlanned=true,initialLoadedAt=Date.now()}) {
  const slide=host.querySelector(`[data-rc-discovery-slide="${panelName}"]`),apple=slide?.querySelector(initialSelector);
  if(!slide||!apple||!carousel||!rotations)return null;
  const doc=host.ownerDocument,view=doc.defaultView||window,root=host.querySelector('[data-rc-discovery-carousel]');
  const box=doc.createElement('div');box.className='rc-new-highlight';apple.before(box);
  const wrap=node=>{const item=doc.createElement('div');item.className='rc-new-highlight__item';item.append(node);return item};
  let original=wrap(apple);box.append(original);
  const availability=doc.createElement('a');availability.className='rc-new-highlight__availability';availability.href=host.querySelector('.rc-trending__explore')?.getAttribute('href')||'/collections/all?sort_by=created-descending';availability.textContent=panelName==='new'?'Explore available new releases':'Explore available products';availability.hidden=true;box.append(availability);
  const cache=new Map([[pinnedHandle,original]]),loadedAt=new Map([[pinnedHandle,initialLoadedAt]]),failures=new Map(),invalidPending=new Set();
  let records=[],current=pinnedHandle,cursor=0,elapsed=0,timer=null,pending=null,planned=null,freshPending=null,needsMeasure=true,transition=null,destroyed=false,maximum=host.classList.contains('rc-trending--hero')?280:380;
  const measuredHeights = new Map();
  let measuredWidth = 0;
  box.style.setProperty('--rc-highlight-height',maximum+'px');rotations.exclude(panelName,current);
  const finish=()=>{if(!transition)return;view.clearTimeout(transition.timer);transition.old.hidden=true;transition.old.inert=true;transition.old.classList.remove('is-leaving');if(transition.old!==original)transition.old.remove();transition.next.inert=false;transition.next.classList.remove('is-entering');transition=null};
  const measure=(item,resize=false)=>{
    if(item.contains(doc.activeElement)){needsMeasure=true;return maximum;}
    const hiddenSlide=slide.hidden;if(hiddenSlide)slide.classList.add('is-measuring');
    const attached=item.parentNode===box,wasHidden=item.hidden,wasInert=item.inert;
    item.hidden=false;item.inert=true;item.classList.add('is-measuring');if(!attached)box.append(item);
    const width=box.getBoundingClientRect().width;
    // Measure the intrinsic native card, not its full-height presentation wrapper.
    const height=Math.ceil(item.firstElementChild?.getBoundingClientRect().height||0);
    item.classList.remove('is-measuring');item.hidden=wasHidden;item.inert=wasInert;if(!attached)item.remove();
    if(hiddenSlide)slide.classList.remove('is-measuring');
    if(!width||!height){needsMeasure=true;return maximum;}
    if(Math.abs(width-measuredWidth)>1){measuredHeights.clear();measuredWidth=width;}
    measuredHeights.set(item,height);
    const retained=new Set([...cache.values(),item]);
    for(const entry of measuredHeights.keys())if(!retained.has(entry))measuredHeights.delete(entry);
    const nextMaximum=Math.max(host.classList.contains('rc-trending--hero')?0:380,...measuredHeights.values());
    if(nextMaximum!==maximum){
      if(resize||carousel.getState().active!==panelName){maximum=nextMaximum;box.style.setProperty('--rc-highlight-height',maximum+'px');carousel.measure();}
      else needsMeasure=true;
    }
    return height;
  };
  const candidates=()=>{
    const visible=rotations.visibleHandles(panelName);
    if(planned&&!visible.includes(planned.handle)&&planned.handle!==current){rotations.exclude(panelName,reservePlanned||elapsed>=5500?[current,planned.handle]:current);return planned;}
    planned=null;const avoid=new Set([...visible,...rotations.upcomingHandles(panelName,reserveUpcoming),current]);
    const handles=[pinnedHandle,...records.map(r=>r.handle)];
    for(let i=0;i<handles.length;i++){const index=(cursor+i)%handles.length,handle=handles[index];if(!avoid.has(handle)&&(!failures.has(handle)||Date.now()-failures.get(handle)>30000)){planned={handle,index};rotations.exclude(panelName,reservePlanned||elapsed>=5500?[current,handle]:current);return planned}}
    return null;
  };
  const markUnavailable=handle=>{if(current!==handle)return;if(carousel.getState().active===panelName){invalidPending.add(handle);return}const old=cache.get(handle);if(old){old.hidden=true;old.inert=true}availability.hidden=false};
  const install=(handle,item,verifiedAt=Date.now())=>{
    if(current===handle&&carousel.getState().active===panelName){freshPending={handle,item,verifiedAt};return}
    invalidPending.delete(handle);const old=cache.get(handle);if(old===original)original=item;
    if(old&&old!==item)old.remove();cache.set(handle,item);loadedAt.set(handle,verifiedAt);
    if(current===handle){item.hidden=false;item.inert=false;box.append(item);availability.hidden=true}
    measure(item);needsMeasure=true;
    for(const [key,entry] of cache){if(cache.size<=7)break;if(key!==pinnedHandle&&key!==current&&key!==handle&&!entry.isConnected)cache.delete(key)}
  };
  const request=(handle)=>{
    if(destroyed||pending)return;pending=handle;
    loadFeature(handle).then(result=>{if(destroyed)return;const node=result?.node||result,verifiedAt=Number.isFinite(result?.verifiedAt)?result.verifiedAt:Date.now();if(!node||Date.now()-verifiedAt>=300000)throw Error('feature-unavailable');const item=wrap(doc.importNode(node,true));item.hidden=true;item.inert=true;install(handle,item,verifiedAt)}).catch(()=>{
      failures.set(handle,Date.now());planned=null;rotations.exclude(panelName,current);
      if(Date.now()-(loadedAt.get(handle)||0)>=300000)markUnavailable(handle)
    }).finally(()=>{pending=null});
  };
  const prepare=()=>{
    const next=candidates();if(!next)return;
    if(cache.has(next.handle)&&Date.now()-(loadedAt.get(next.handle)||0)<300000)return;
    request(next.handle);
  };
  const tick=()=>{
    if(destroyed)return;const outer=carousel.getState();
    if(outer.active!==panelName){elapsed=0;finish();for(const handle of invalidPending)markUnavailable(handle);invalidPending.clear();if(freshPending){install(freshPending.handle,freshPending.item,freshPending.verifiedAt);freshPending=null}if(needsMeasure){for(const item of cache.values())measure(item);needsMeasure=false}if(Date.now()-(loadedAt.get(pinnedHandle)||0)>=300000&&(!failures.has(pinnedHandle)||Date.now()-failures.get(pinnedHandle)>30000))request(pinnedHandle)}
    else if(outer.paused||outer.transitioning||outer.reduced){elapsed=0;finish()}
    else{
      elapsed+=500;prepare();
      if(elapsed>=6000&&!transition){const choice=candidates(),item=choice&&cache.get(choice.handle);if(item&&Date.now()-(loadedAt.get(choice.handle)||0)<300000&&measure(item)<=maximum){
        const old=cache.get(current);old.inert=true;item.hidden=false;item.inert=true;box.append(item);current=choice.handle;cursor=choice.index+1;planned=null;rotations.exclude(panelName,current);elapsed=0;availability.hidden=true;
        transition={old,next:item,timer:null};old.classList.add('is-leaving');item.classList.add('is-entering');view.requestAnimationFrame(()=>view.requestAnimationFrame(()=>{if(transition&&!destroyed)item.classList.remove('is-entering')}));transition.timer=view.setTimeout(finish,460);
      }}
    }
    timer=view.setTimeout(tick,500);
  };
  const suspend=()=>{finish();elapsed=0};
  const interaction=event=>{if(event.target.closest?.('a,button,input,select,textarea'))suspend()};
  const onResize=()=>{if(carousel.getState().transitioning||[...cache.values()].some(item=>item.contains(doc.activeElement))){needsMeasure=true;return}for(const item of cache.values())measure(item,true);carousel.measure()};
  host.addEventListener('rc:discovery-changing',suspend);root.addEventListener('focusin',interaction);root.addEventListener('pointerover',interaction);view.addEventListener('resize',onResize);
  view.requestAnimationFrame(()=>{if(!destroyed){measure(original,true);carousel.measure()}});
  timer=view.setTimeout(tick,500);
  return{invalidate(handles){for(const handle of handles){failures.set(handle,Date.now());loadedAt.set(handle,0);markUnavailable(handle);if(planned?.handle===handle)planned=null}records=records.filter(r=>!handles.includes(r.handle));rotations.exclude(panelName,current)},setProducts(products){records=products.filter(r=>r.handle!==pinnedHandle);prepare()},currentHandle:()=>current,destroy(){destroyed=true;view.clearTimeout(timer);finish();host.removeEventListener('rc:discovery-changing',suspend);root.removeEventListener('focusin',interaction);root.removeEventListener('pointerover',interaction);view.removeEventListener('resize',onResize)}};
}

export function mountDiscovery(host, { pinnedHandle = null } = {}) {
  const dialog = host.querySelector('[data-rc-discovery-dialog]');
  if (!dialog || typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function') return null;
  const doc = host.ownerDocument;
  const tabs = [...host.querySelectorAll('[data-rc-discovery-tab]')];
  const panels = [...host.querySelectorAll('[data-rc-discovery-panel]')];
  const openers = [...host.querySelectorAll('[data-rc-discovery-open]')];
  const closeButton = host.querySelector('[data-rc-discovery-close]');
  const previews = host.querySelector('[data-rc-discovery-previews]');
  let opener = null;
  let restoreFocus = true;
  const activate = (name, focus = false) => {
    const selected = tabs.find(tab => tab.dataset.rcDiscoveryTab === name);
    if (!selected) return;
    for (const tab of tabs) {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of panels) panel.hidden = panel.dataset.rcDiscoveryPanel !== name;
    if (focus) selected.focus({ preventScroll: true });
  };
  const close = (restore = true) => {
    if (!dialog.open) return;
    restoreFocus = restore;
    dialog.close();
  };
  const onClose = () => { if (restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true }); };
  const onCancel = event => { event.preventDefault(); close(true); };
  const onOpen = event => {
    const button = event.currentTarget;
    opener = button;
    restoreFocus = true;
    activate(button.dataset.rcDiscoveryOpen);
    if (!dialog.open) dialog.showModal();
    tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus({ preventScroll: true });
  };
  const onTabClick = event => activate(event.currentTarget.dataset.rcDiscoveryTab, true);
  const onTabKey = event => {
    const current = tabs.indexOf(event.currentTarget);
    const rtl = host.getAttribute('dir') === 'rtl';
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else if (event.key === 'ArrowRight') next = (current + (rtl ? -1 : 1) + tabs.length) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current + (rtl ? 1 : -1) + tabs.length) % tabs.length;
    else return;
    event.preventDefault();
    activate(tabs[next].dataset.rcDiscoveryTab, true);
  };
  const onBackdrop = event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close(true);
  };
  // The native theme emits this after its real modal opens. Leave its cart/form and focus behavior intact.
  const onNativeDialog = event => {
    const native = event.target;
    if (!dialog.open || !native?.matches?.('#quick-add-dialog, cart-drawer-component')) return;
    if (native.querySelector('dialog')?.open) close(false);
  };
  const syncPreviews = (cards = [], prepend = false) => {
    if(!previews)return;
    previews.dataset.rcPrepend=String(prepend);
    for(const card of cards){if(!card.teaser||card.handle===pinnedHandle)continue;previews.append(doc.importNode(card.teaser,true))}
    host.dispatchEvent(new CustomEvent('rc:discovery-previews'));
  };
  for (const button of openers) { button.hidden = false; button.addEventListener('click', onOpen); }
  for (const tab of tabs) { tab.addEventListener('click', onTabClick); tab.addEventListener('keydown', onTabKey); }
  const onCloseClick = () => close(true);
  closeButton?.addEventListener('click', onCloseClick);
  dialog.addEventListener('cancel', onCancel);
  dialog.addEventListener('close', onClose);
  dialog.addEventListener('click', onBackdrop);
  doc.addEventListener('dialog:open', onNativeDialog, true);
  host.querySelector('[data-rc-discovery-fallback]')?.setAttribute('hidden', '');
  syncPreviews();
  return { activate, close, syncPreviews, destroy() {
    close(false);
    for (const button of openers) button.removeEventListener('click', onOpen);
    for (const tab of tabs) { tab.removeEventListener('click', onTabClick); tab.removeEventListener('keydown', onTabKey); }
    closeButton?.removeEventListener('click', onCloseClick);
    dialog.removeEventListener('cancel', onCancel);
    dialog.removeEventListener('close', onClose);
    dialog.removeEventListener('click', onBackdrop);
    doc.removeEventListener('dialog:open', onNativeDialog, true);
  } };
}

// Hero and Discover share this bounded CJ request; cards retain their original verification time.
const cjRequests = new Map();
async function loadCjSelection(api, request) {
  const {signal, ...parameters}=request;
  if(signal?.aborted)throw new DOMException('Aborted','AbortError');
  const key=JSON.stringify([parameters.origin,parameters.rootUrl,parameters.locale,parameters.country,parameters.currency,parameters.handles]);
  let entry=cjRequests.get(key);
  if(!entry||Date.now()-entry.createdAt>=300000){
    for(const [k,value] of cjRequests)if(Date.now()-value.createdAt>=300000)cjRequests.delete(k);
    entry={createdAt:Date.now()};
    entry.promise=api.loadTrendingProducts({...parameters,includeFeature:true,featureKind:'cj'}).then(response=>({...response,verifiedAt:Date.now()}));
    cjRequests.set(key,entry);const clear=()=>{if(cjRequests.get(key)===entry)cjRequests.delete(key)};entry.promise.then(clear,clear);
  }
  const response=await entry.promise;
  if(signal?.aborted)throw new DOMException('Aborted','AbortError');
  return response;
}

// Adapt only the two product slots to the unchanged native hero carousel.
export function mountHeroSelection(host,{importService=url=>import(url),location=window.location}={}) {
  if(host.dataset.rcHeroMounted==='true')return null;
  const hero=host.closest('ricordo-carousel'),slide=host.closest('.rc-slide'),doc=host.ownerDocument,view=doc.defaultView||window;
  const discovery=host.closest('.shopify-section')?.querySelector('[data-rc-trending]');
  let config,serviceUrl;
  try{config=parseTrendingConfig(discovery?.querySelector('[data-rc-trending-config]')?.textContent||'');serviceUrl=safeServiceUrl(discovery.dataset.serviceUrl,location)}catch{return null}
  if(!hero||!slide||!config.cjSelection.handles.length)return null;
  host.dataset.rcHeroMounted='true';host.hidden=true;
  const reduced=view.matchMedia('(prefers-reduced-motion: reduce)');
  let hovered=false,destroyed=false,pending=null,timer=null,refreshTimer=null,loading=false;
  const controller=new AbortController();
  const focused=()=>host.contains(doc.activeElement);
  const active=()=>slide.classList.contains('is-active')&&!slide.inert;
  const adapter={getState:()=>({active:active()?'hero':'',transitioning:false,reduced:reduced.matches,paused:Boolean(hero.userPaused||doc.hidden||hovered||focused())}),measure(){}};
  const rotations=mountProductRotations(host,adapter);
  const onOver=event=>{if(event.target.closest?.('a,button'))hovered=true};
  const onOut=event=>{if(!event.relatedTarget||!host.contains(event.relatedTarget)||!event.relatedTarget.closest?.('a,button'))hovered=false};
  host.addEventListener('pointerover',onOver);host.addEventListener('pointerout',onOut);
  let servicePromise;const service=()=>servicePromise||=importService(serviceUrl);
  const apply=()=>{
    if(pending&&Date.now()-pending.verifiedAt>=300000){pending=null;view.clearTimeout(refreshTimer);refresh();}
    if(pending&&!hovered&&!focused()&&(!active()||!host.querySelector('.rc-product-rotation__slots'))){
      host.hidden=pending.cards.length===0;rotations.replace('hero',pending.cards.map(card=>card.teaser));pending=null;
    }
    timer=view.setTimeout(apply,500);
  };
  const refresh=async()=>{
    if(loading||destroyed)return;loading=true;view.clearTimeout(refreshTimer);
    try{const api=await service();const response=await loadCjSelection(api,{rootUrl:config.rootUrl,origin:location.origin,locale:config.locale,country:config.country,currency:config.currency,handles:config.cjSelection.handles,signal:controller.signal});if(!destroyed)pending=response}catch{/* Keep the collection link available on network failure. */}
    loading=false;if(!destroyed)refreshTimer=view.setTimeout(refresh,300000);
  };
  apply();refresh();
  return{rotations,refresh,destroy(){destroyed=true;controller.abort();view.clearTimeout(timer);view.clearTimeout(refreshTimer);rotations.destroy();host.removeEventListener('pointerover',onOver);host.removeEventListener('pointerout',onOut);delete host.dataset.rcHeroMounted;}};
}

export function mountTrending(host, { importService = (url) => import(url), location = window.location } = {}) {
  if (host.dataset.rcTrendingMounted === 'true') return null;
  let config;
  let serviceUrl;
  try {
    config = parseTrendingConfig(host.querySelector('[data-rc-trending-config]')?.textContent || '');
    serviceUrl = safeServiceUrl(host.dataset.serviceUrl, location);
  } catch { return null; }
  let selector = host.querySelector('[data-rc-trending-region]');
  let label = host.querySelector('[data-rc-trending-region-label]');
  const ranked = host.querySelector('[data-rc-trending-ranked]');
  const arrivals = host.querySelector('[data-rc-trending-new]');
  const status = host.querySelector('[data-rc-trending-status]');
  const period = host.querySelector('[data-rc-trending-period]');
  const cjDisclaimer = host.querySelector('[data-rc-cj-disclaimer]');
  if (!ranked || !arrivals || !status) return null;
  host.dataset.rcTrendingMounted = 'true';
  const homeClean = host.hasAttribute('data-rc-home-clean');
  const homeBestHandles = homeClean ? [...new Set((host.dataset.rcHomeBestHandles || '').split('|').filter(handle => HANDLE.test(handle)))].slice(0,2) : [];
  const discovery = mountDiscovery(host, { pinnedHandle: config.pinnedHandle });
  const carousel = mountDiscoveryCarousel(host);
  const rotations = mountProductRotations(host, carousel);
  const refreshCarousel = () => { rotations?.refresh(); carousel?.measure(); };
  host.addEventListener('rc:discovery-previews', refreshCarousel);
  let generation = 0;
  let controller;
  const arrivalsController = new AbortController();
  let destroyed = false;
  let servicePromise;
  const service = () => servicePromise ||= importService(serviceUrl);
  const context = { rootUrl: config.rootUrl, origin: location.origin, locale: config.locale, country: config.country, currency: config.currency };
  let cjHighlight = null, pendingCj = null, cjLoaded = false;
  let pendingHomeBest = null, loadingHomeBest = false;
  const homeBestCards = new Map([...host.querySelectorAll('[data-rc-discovery-slide="bestsellers"] [data-rc-product-handle]')].filter(node=>homeBestHandles.includes(node.dataset.rcProductHandle)).map(node=>[node.dataset.rcProductHandle,{handle:node.dataset.rcProductHandle,teaser:node.cloneNode(true)}]));
  const loadHomeBest = async () => {
    if (!homeClean || !homeBestHandles.length || destroyed || loadingHomeBest) return;
    loadingHomeBest = true;
    try {
      const api = await service();
      const response = await api.loadTrendingProducts({...context,handles:homeBestHandles,signal:arrivalsController.signal});
      if (destroyed) return;
      let changed = false;
      for (const card of response.cards || []) if (homeBestHandles.includes(card.handle) && card.teaser) {homeBestCards.set(card.handle,card);changed=true;}
      for (const error of response.errors || []) if (homeBestHandles.includes(error.handle) && ['not-available','http-404','http-410'].includes(error.code)) {homeBestCards.delete(error.handle);changed=true;}
      if(changed)pendingHomeBest = {cards:homeBestHandles.map(handle=>homeBestCards.get(handle)).filter(Boolean),verifiedAt:Date.now()};
    } catch { /* A transient failure keeps the last valid pair and never invents availability. */ }
    finally { loadingHomeBest = false; }
  };
  const clearCj = () => {
    cjHighlight?.destroy(); cjHighlight=null; pendingCj=null; cjLoaded=false;
    const slide=host.querySelector('[data-rc-discovery-slide="bestsellers"]');
    slide.querySelector('.rc-new-highlight')?.remove(); slide.querySelector('[data-rc-cj-feature]')?.remove();
    rotations?.exclude('bestsellers',[]);
    slide.classList.toggle('rc-discovery-card--with-top-seller',Boolean(slide.querySelector('[data-rc-top-seller]')));
  };
  const applyCj = (cards,verifiedAt=Date.now()) => {
    if(config.selectionSource!=='cj-listed'||destroyed||homeClean)return;
    clearCj(); cjLoaded=true;
    const slide=host.querySelector('[data-rc-discovery-slide="bestsellers"]');
    const first=cards.find(card=>card.feature);
    const ordered=first?[...cards.filter(card=>card.handle!==first.handle),...(cards.length>2?[first]:[])]:cards;
    rotations?.replace('bestsellers',ordered.map(card=>card.teaser));
    let message=slide.querySelector('[data-rc-cj-empty]');
    if(!message){message=host.ownerDocument.createElement('p');message.className='rc-discovery-card__empty';message.dataset.rcCjEmpty='';slide.append(message)}
    message.textContent=config.cjSelection.empty; message.hidden=cards.length>0;
    if(first){
      slide.querySelector('[data-rc-product-rotation]').after(host.ownerDocument.importNode(first.feature,true));
      slide.classList.add('rc-discovery-card--with-top-seller');
      const cachedAt=verifiedAt,features=new Map(cards.filter(card=>card.feature).map(card=>[card.handle,card.feature]));
      cjHighlight=mountNewHighlight(host,carousel,rotations,{pinnedHandle:first.handle,panelName:'bestsellers',initialSelector:':scope>[data-rc-cj-feature]',reserveUpcoming:0,reservePlanned:false,initialLoadedAt:verifiedAt,loadFeature:async handle=>{
        if(Date.now()-cachedAt<300000&&features.has(handle))return {node:features.get(handle),verifiedAt:cachedAt};
        const api=await service();if(destroyed||config.selectionSource!=='cj-listed')return null;
        const response=await api.loadTrendingProducts({...context,handles:[handle],includeFeature:true,featureKind:'cj',signal:arrivalsController.signal});
        return response.cards[0]?.feature||null;
      }});
      cjHighlight?.setProducts(cards);
    }
    carousel?.measure();
  };
  const choose = async () => {
    const isCj=config.selectionSource==='cj-listed';
    const group = isCj ? {handles:config.cjSelection.handles} : config.groups.find(item => item.code === (selector?.value || 'GLOBAL')) || config.groups.find(item=>item.code==='GLOBAL');
    if (!group) return;
    const run = ++generation;
    controller?.abort();
    controller = new AbortController();
    ranked.replaceChildren();
    if (period) period.hidden = true;
    ranked.setAttribute('aria-busy', 'true');
    status.textContent = config.labels.loading;
    if(cjDisclaimer){cjDisclaimer.hidden=!isCj||!config.cjSelection.disclaimer;cjDisclaimer.textContent=config.cjSelection.disclaimer;}
    try {
      const api = await service();
      if (run !== generation || destroyed) return;
      const response = isCj ? await loadCjSelection(api,{...context,handles:group.handles,signal:controller.signal}) : await api.loadTrendingProducts({...context,handles:group.handles,signal:controller.signal});
      if (run !== generation || destroyed) return;
      const count = appendNativeCards(ranked, response.cards, host.ownerDocument);
      if (period) period.hidden = isCj || count === 0;
      if(isCj){const verifiedAt=response.verifiedAt||Date.now();if(cjLoaded&&(carousel?.getState().active==='bestsellers'||host.querySelector('[data-rc-discovery-dialog]')?.open))pendingCj={cards:response.cards,verifiedAt};else applyCj(response.cards,verifiedAt);}
      status.textContent = count ? config.labels.ready : response.errors?.length ? config.labels.error : isCj ? config.cjSelection.empty : config.labels.empty;
    } catch (error) {
      if (run !== generation || destroyed || error?.name === 'AbortError') return;
      status.textContent = config.labels.error;
    } finally {
      if (run === generation && !destroyed) ranked.removeAttribute('aria-busy');
    }
  };
  const syncRegions = (labelText = '') => {
    const available=config.selectionSource==='sales'&&config.feedReady&&config.groups.length>1,previous=selector?.value||'GLOBAL';
    if(available&&!selector){
      const id=host.querySelector('[data-rc-discovery-dialog]').id+'-live-region';
      label=host.ownerDocument.createElement('label');label.className='rc-trending__label';label.htmlFor=id;label.dataset.rcTrendingRegionLabel='';
      selector=host.ownerDocument.createElement('select');selector.className='rc-trending__select';selector.id=id;selector.dataset.rcTrendingRegion='';ranked.before(label,selector);
    }
    if(!selector)return;
    selector.removeEventListener('change',choose);selector.replaceChildren();
    for(const group of config.groups){const option=host.ownerDocument.createElement('option');option.value=group.code;option.textContent=group.label;selector.append(option)}
    selector.value=config.groups.some(g=>g.code===previous)?previous:'GLOBAL';selector.hidden=!available;
    if(label){label.hidden=!available;if(labelText)label.textContent=labelText;else if(!label.textContent)label.textContent='Best sellers by country'}
    if(available)selector.addEventListener('change',choose);
  };
  syncRegions();
  const records=new Map(),knownHandles=new Set(),pendingIneligible=new Set();
  let refreshPageCursor=0;
  const pendingPages=[];
  let nextPage=1, loadingPage=false, lastPageAt=0, refreshTimer=null, applyTimer=null, pendingRefresh=null, pendingSnapshot=null;
  const highlight=homeClean?null:mountNewHighlight(host,carousel,rotations,{pinnedHandle:config.pinnedHandle,loadFeature:async handle=>{
    const api=await service();if(destroyed)return null;
    const response=await api.loadTrendingProducts({...context,handles:[handle],includeFeature:true,signal:arrivalsController.signal});
    return response.cards[0]?.feature||null;
  }});
  const syncWindow=()=>{
    const retained=new Set(rotations?.retainedHandles('new')||[]),window=new Set(rotations?.poolHandles('new')||[]);
    for(const handle of [...records.keys()])if(!retained.has(handle))records.delete(handle);
    for(const li of arrivals.querySelectorAll('[data-rc-trending-handle]'))if(li.dataset.rcTrendingHandle!==config.pinnedHandle&&!window.has(li.dataset.rcTrendingHandle))li.remove();
    const shown=new Set([...arrivals.querySelectorAll('[data-rc-trending-handle]')].map(n=>n.dataset.rcTrendingHandle));
    appendNativeCards(arrivals,[...records.values()].filter(r=>window.has(r.handle)),host.ownerDocument,shown);
    highlight?.setProducts([...records.values()].filter(r=>window.has(r.handle)));
  };
  host.addEventListener('rc:discovery-window',syncWindow);
  const applyCards=(incoming,prepend=false)=>{
    const cards=incoming.filter(card=>!knownHandles.has(card.handle)||records.has(card.handle));
    for(const card of incoming){knownHandles.add(card.handle);pendingIneligible.delete(card.handle)}
    const oldHandles=new Set([...arrivals.querySelectorAll('[data-rc-trending-handle]')].map(n=>n.dataset.rcTrendingHandle));
    if(prepend)for(const card of cards){const li=[...arrivals.querySelectorAll('[data-rc-trending-handle]')].find(n=>n.dataset.rcTrendingHandle===card.handle);if(li)li.replaceChildren(host.ownerDocument.importNode(card.element,true))}
    appendNativeCards(arrivals,cards,host.ownerDocument,new Set([...oldHandles,...(config.pinnedHandle?[config.pinnedHandle]:[])]));
    if(prepend){const incoming=new Map(cards.map(card=>[card.handle,card]));for(const [handle,card] of records)if(!incoming.has(handle))incoming.set(handle,card);records.clear();for(const [handle,card] of incoming)records.set(handle,card)}
    else for(const card of cards)records.set(card.handle,card);
    discovery?.syncPreviews(cards,prepend);
    syncWindow();
  };
  const loadPage=async()=>{
    if(destroyed||loadingPage||pendingPages.length||nextPage===null||Date.now()-lastPageAt<2000)return;
    loadingPage=true;lastPageAt=Date.now();const page=nextPage;
    try{const api=await service();const response=await api.loadNewReleaseProducts({...context,page,limit:24,excludeHandles:config.pinnedHandle?[config.pinnedHandle]:[],signal:arrivalsController.signal});
      if(destroyed)return;if(response.errors?.length){lastPageAt=Date.now()+28000;return}
      nextPage=response.nextPage;for(const handle of response.ineligibleHandles||[])pendingIneligible.add(handle);
      if(response.cards.length&&carousel?.getState().active==='new'&&(rotations?.poolHandles('new').length||0)>0)pendingPages.push({cards:response.cards,page,loadedAt:Date.now()});else applyCards(response.cards);
    }catch{/* Keep the current valid selection and retry only after the ordinary demand interval. */lastPageAt=Date.now()+28000}
    finally{loadingPage=false}
  };
  const onMore=()=>{loadPage()};host.addEventListener('rc:discovery-more',onMore);
  const newReleases=loadPage();
  const topSignature=node=>node?`${node.dataset.rcTopSeller}|${node.querySelector('input[name="id"]')?.value||''}|${node.querySelector('.rc-discovery-top-seller__price')?.textContent||''}`:'';
  const applyPending=()=>{
    if(destroyed)return;
    const outer=carousel?.getState();
    if(!outer?.transitioning&&!host.querySelector('[data-rc-discovery-dialog]')?.open){
      if(pendingHomeBest&&outer.active!=='bestsellers'){
        const batch=pendingHomeBest;pendingHomeBest=null;
        if(Date.now()-batch.verifiedAt<300000){rotations?.replace('bestsellers',batch.cards.map(card=>card.teaser));const empty=host.querySelector('[data-rc-home-best-empty]');if(empty)empty.hidden=batch.cards.length>0;carousel?.measure();}
        else loadHomeBest();
      }
      if(outer.active==='new'&&(rotations?.poolHandles('new').length||0)<3&&!host.ownerDocument.hidden)loadPage();
      if(pendingCj&&outer.active!=='bestsellers'){const batch=pendingCj;pendingCj=null;if(Date.now()-batch.verifiedAt<300000)applyCj(batch.cards,batch.verifiedAt);else choose();}
      if(pendingPages.length&&outer.active!=='new'){
        const fresher=new Set([...(pendingRefresh?.cards||[]).map(card=>card.handle),...pendingIneligible]),cards=[];
        for(const batch of pendingPages.splice(0)){
          if(Date.now()-batch.loadedAt>=300000){nextPage=Math.min(nextPage??Infinity,batch.page);continue;}
          cards.push(...batch.cards.filter(card=>!fresher.has(card.handle)));
        }
        if(cards.length)applyCards(cards);
      }
      if(pendingIneligible.size&&outer.active!=='new'){const handles=[...pendingIneligible];rotations?.remove('new',handles);highlight?.invalidate(handles);for(const handle of handles){knownHandles.delete(handle);records.delete(handle)}pendingIneligible.clear();syncWindow()}
      if(pendingRefresh&&outer.active!=='new'){applyCards(pendingRefresh.cards,true);if(pendingRefresh.nextPage!==null)nextPage=Math.min(nextPage??Infinity,pendingRefresh.nextPage);pendingRefresh=null}
      if(pendingSnapshot){
        for(const name of ['bestsellers','trending']){
          const data=pendingSnapshot.panels[name];if(!data||outer.active===name)continue;
          if(name==='bestsellers'&&homeClean){config=parseTrendingConfig(pendingSnapshot.config);syncRegions(pendingSnapshot.regionLabel);choose();delete pendingSnapshot.panels[name];continue;}
          rotations?.replace(name,data.teasers);
          if(name==='bestsellers'){
            clearCj();host.querySelector('[data-rc-cj-empty]')?.remove();
            const slide=host.querySelector('[data-rc-discovery-slide="bestsellers"]'),old=slide.querySelector('[data-rc-top-seller]');
            if(topSignature(old)!==topSignature(data.top)){old?.remove();if(data.top)slide.querySelector('[data-rc-product-rotation]').after(host.ownerDocument.importNode(data.top,true))}
            slide.classList.toggle('rc-discovery-card--with-top-seller',Boolean(data.top));slide.querySelector('.rc-discovery-card__intro').textContent=data.intro;
            const button=slide.querySelector('[data-rc-discovery-open]');if(button&&['new','bestsellers'].includes(data.choiceTarget)){button.dataset.rcDiscoveryOpen=data.choiceTarget;const label=button.querySelector('.visually-hidden');if(label)label.textContent=data.choice;else button.textContent=data.choice}
            config=parseTrendingConfig(pendingSnapshot.config);
            if(config.selectionSource==='cj-listed'){
              const intro=slide.querySelector('.rc-discovery-card__intro'),p=host.ownerDocument.createElement('p'),note=host.ownerDocument.createElement('span');p.textContent=config.cjSelection.title;note.className='rc-discovery-card__note';note.textContent=config.cjSelection.note;intro.replaceChildren(p);if(config.cjSelection.note)intro.append(note);
            }
            syncRegions(pendingSnapshot.regionLabel);choose();
          }
          delete pendingSnapshot.panels[name];carousel?.measure();
        }
        if(!Object.keys(pendingSnapshot.panels).length)pendingSnapshot=null;
      }
    }
    applyTimer=setTimeout(applyPending,1000);
  };
  const refresh=async()=>{
    if(destroyed)return;
    if(!host.ownerDocument.hidden){
      loadHomeBest();
      try{const api=await service();
        const pages=[...new Set([...records.values()].map(card=>card.sourcePage).filter(page=>Number.isInteger(page)&&page>1))].sort((a,b)=>a-b),extra=[];
        for(let i=0;i<Math.min(2,pages.length);i++)extra.push(pages[(refreshPageCursor+i)%pages.length]);refreshPageCursor+=2;
        const [fresh,snapshot,...others]=await Promise.allSettled([
          api.loadNewReleaseProducts({...context,page:1,limit:24,excludeHandles:config.pinnedHandle?[config.pinnedHandle]:[],signal:arrivalsController.signal}),
          api.loadDiscoverySnapshot({...context,signal:arrivalsController.signal}),
          ...extra.map(page=>api.loadNewReleaseProducts({...context,page,limit:24,excludeHandles:config.pinnedHandle?[config.pinnedHandle]:[],signal:arrivalsController.signal}))
        ]);if(destroyed)return;
        const valid=[fresh,...others].filter(result=>result.status==='fulfilled'&&!result.value.errors?.length).map(result=>result.value);
        for(const result of valid)for(const handle of result.ineligibleHandles||[])pendingIneligible.add(handle);
        if(valid.length)pendingRefresh={cards:valid.flatMap(result=>result.cards),nextPage:fresh.status==='fulfilled'&&!fresh.value.errors?.length?fresh.value.nextPage:null};
        if(snapshot.status==='fulfilled'){parseTrendingConfig(snapshot.value.config);pendingSnapshot=snapshot.value}
      }catch{/* A transient failure never clears a valid current presentation. */}
    }
    refreshTimer=setTimeout(refresh,300000);
  };
  if(config.selectionSource==='cj-listed')choose();
  loadHomeBest();
  refreshTimer=setTimeout(refresh,300000);applyTimer=setTimeout(applyPending,1000);
  const destroy = () => {
    destroyed = true;
    generation++;
    controller?.abort();
    arrivalsController.abort();cjHighlight?.destroy();
    clearTimeout(refreshTimer);clearTimeout(applyTimer);
    host.removeEventListener('rc:discovery-more',onMore);host.removeEventListener('rc:discovery-window',syncWindow);highlight?.destroy();
    selector?.removeEventListener('change', choose);
    discovery?.destroy();
    rotations?.destroy();
    carousel?.destroy();
    host.removeEventListener('rc:discovery-previews', refreshCarousel);
    delete host.dataset.rcTrendingMounted;
  };
  return { destroy, choose, newReleases, refresh, config, discovery, carousel, rotations, highlight };
}

const mounted = new WeakMap();
const mountedHeroes = new WeakMap();
function mountAll(root) {
  for(const host of root.querySelectorAll('[data-rc-hero-selection]')){const instance=mountHeroSelection(host);if(instance)mountedHeroes.set(host,instance)}
  const hosts = [...root.querySelectorAll('[data-rc-trending]')];
  if (root.matches?.('[data-rc-trending]')) hosts.unshift(root);
  for (const host of hosts) {
    const instance = mountTrending(host);
    if (instance) mounted.set(host, instance);
  }
}
if (typeof document !== 'undefined') {
  mountAll(document);
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const host of event.target.querySelectorAll('[data-rc-trending]')) mounted.get(host)?.destroy();
    for (const host of event.target.querySelectorAll('[data-rc-hero-selection]')) mountedHeroes.get(host)?.destroy();
  });
}
